/**
 * facets.js — progressive enhancement for the collection and search grids.
 *
 * The markup it enhances works entirely without JavaScript: `snippets/facets.liquid`
 * renders a plain GET form with a visible "Apply filters" submit button, and the
 * pagination is ordinary links. This file takes over only once it has run, and
 * announces that by setting `data-facets-enhanced` on the grid wrapper — which is
 * also the hook that hides the now-redundant submit button. If the script 404s or
 * throws, the page degrades to full navigations rather than breaking.
 *
 * When it is in charge it uses the Section Rendering API (`?section_id=`) to fetch
 * just this section's HTML and swap it in, then rewrites the address bar with
 * history.replaceState so a filtered view can be copied, bookmarked and shared.
 *
 * Contract with the Liquid (see main-collection.liquid / main-search.liquid):
 *   [data-facets]            wrapper around the whole toolbar + results; also
 *                            carries data-facets-section="{{ section.id }}"
 *   [data-facets-form]       the GET form holding filters, sort and hidden params
 *   [data-facets-results]    the region replaced on every update
 *   [data-facets-link]       an anchor (filter chip, pagination link) that should
 *                            be fetched instead of navigated to
 *   [data-facets-price]      a price-range number input, debounced rather than
 *                            fired on every keystroke
 *   [data-facets-group]      one filter's <details> disclosure
 */
(function () {
  'use strict';

  var PRICE_DEBOUNCE_MS = 500;

  /** Serialize a form to a query string, dropping empty values so an untouched
   *  price box never turns into `filter.v.price.gte=` in a shareable URL. */
  function serialize(form) {
    var params = new URLSearchParams();

    new FormData(form).forEach(function (value, key) {
      if (typeof value === 'string' && value.trim() === '') return;
      params.append(key, value);
    });

    return params.toString();
  }

  function baseUrl(form) {
    var action = form.getAttribute('action') || window.location.pathname;
    return action.split('?')[0];
  }

  function withSectionId(url, sectionId) {
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + 'section_id=' + encodeURIComponent(sectionId);
  }

  function Facets(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-facets-section');
    this.priceTimer = null;
    this.requestToken = 0;

    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    root.addEventListener('change', this.onChange);
    root.addEventListener('input', this.onInput);
    root.addEventListener('submit', this.onSubmit);
    root.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    root.setAttribute('data-facets-enhanced', '');
  }

  Facets.prototype.form = function () {
    return this.root.querySelector('[data-facets-form]');
  };

  Facets.prototype.results = function () {
    return this.root.querySelector('[data-facets-results]');
  };

  Facets.prototype.onChange = function (event) {
    var target = event.target;
    if (!target || !target.name) return;
    if (target.matches('[data-facets-price]')) return; // handled by onInput
    if (!target.closest('[data-facets-form]')) return;

    this.closeGroups();
    this.apply();
  };

  Facets.prototype.onInput = function (event) {
    var target = event.target;
    if (!target || !target.matches('[data-facets-price]')) return;

    var self = this;
    window.clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(function () {
      self.apply();
    }, PRICE_DEBOUNCE_MS);
  };

  Facets.prototype.onSubmit = function (event) {
    if (!event.target.matches('[data-facets-form]')) return;
    event.preventDefault();
    this.closeGroups();
    this.apply();
  };

  Facets.prototype.onClick = function (event) {
    var link = event.target.closest ? event.target.closest('a[data-facets-link]') : null;
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.closeGroups();
    this.render(link.getAttribute('href'), true);
  };

  /** Only one filter dropdown open at a time — they overlap on desktop. */
  Facets.prototype.onDocumentClick = function (event) {
    var groups = this.root.querySelectorAll('[data-facets-group][open]');

    for (var i = 0; i < groups.length; i++) {
      if (!groups[i].contains(event.target)) groups[i].removeAttribute('open');
    }
  };

  Facets.prototype.onKeydown = function (event) {
    if (event.key !== 'Escape') return;
    this.closeGroups();
  };

  Facets.prototype.closeGroups = function () {
    var groups = this.root.querySelectorAll('[data-facets-group][open]');

    for (var i = 0; i < groups.length; i++) {
      groups[i].removeAttribute('open');
    }
  };

  Facets.prototype.apply = function () {
    var form = this.form();
    if (!form) return;

    var query = serialize(form);
    var url = baseUrl(form) + (query ? '?' + query : '');
    this.render(url, false);
  };

  /**
   * Fetch `url` rendered as just this section and swap it in.
   * @param {string} url - The shopper-facing URL the new state lives at.
   * @param {boolean} scroll - Scroll the results back into view (pagination).
   */
  Facets.prototype.render = function (url, scroll) {
    var self = this;
    var token = ++this.requestToken;

    this.root.setAttribute('data-facets-loading', '');

    fetch(withSectionId(url, this.sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // A newer interaction has already started; discard this stale response.
        if (token !== self.requestToken) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var incoming = parsed.querySelector('[data-facets]');
        if (!incoming) throw new Error('No [data-facets] in section response');

        var activeId = document.activeElement ? document.activeElement.id : null;
        var selectionStart = null;

        if (activeId && document.activeElement.selectionStart != null) {
          selectionStart = document.activeElement.selectionStart;
        }

        self.root.innerHTML = incoming.innerHTML;
        self.root.removeAttribute('data-facets-loading');

        window.history.replaceState({ facets: true }, '', url);

        if (activeId) {
          var restored = document.getElementById(activeId);

          if (restored) {
            restored.focus();
            if (selectionStart != null && restored.setSelectionRange) {
              try {
                restored.setSelectionRange(selectionStart, selectionStart);
              } catch (error) {
                /* number inputs reject setSelectionRange in some browsers */
              }
            }
          }
        }

        if (scroll) {
          var top = self.root.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }

        self.root.dispatchEvent(
          new CustomEvent('facets:rendered', { bubbles: true, detail: { url: url } })
        );
      })
      .catch(function () {
        // Anything unexpected — network, a redirect, a changed section id —
        // falls back to the plain navigation the no-JS path would have done.
        self.root.removeAttribute('data-facets-loading');
        window.location.href = url;
      });
  };

  function init() {
    var roots = document.querySelectorAll('[data-facets]:not([data-facets-enhanced])');

    for (var i = 0; i < roots.length; i++) {
      new Facets(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders a section into the DOM without a page load.
  document.addEventListener('shopify:section:load', init);
})();
